import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Text, Skeleton } from "@chakra-ui/react";
import Chart from "react-apexcharts";
import { fetchDistrictReportsAction } from "state/actions/analyticsActions";

const DataByDistrictHeatmap = () => {
    const dispatch = useDispatch();
    const { districtReports, loading, error } = useSelector((state) => state.analytics);
    const [selectedMetric, setSelectedMetric] = useState("packages_created");

    useEffect(() => {
        dispatch(fetchDistrictReportsAction());
    }, [dispatch]);

    const heatmapData = [
        {
            name: "Packages Created",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.packages_created
            }))
        },
        {
            name: "Deliveries",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.deliveries
            }))
        },
        {
            name: "Completed Packages",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.completed_packages
            }))
        },
        {
            name: "Claims",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.claims
            }))
        },
        {
            name: "Drivers",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.drivers
            }))
        },
        {
            name: "Users",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.users
            }))
        },
        {
            name: "Transactions",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.transactions
            }))
        },
        {
            name: "Vehicles",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.vehicles
            }))
        },
        {
            name: "Businesses",
            data: districtReports.map(district => ({
                x: district.district || "Unknown",
                y: district.businesses
            }))
        }
    ];

    const chartOptions = {
        chart: {
            type: "heatmap",
            height: 350,
        },
        dataLabels: {
            enabled: false,
        },
        plotOptions: {
            heatmap: {
                colorScale: {
                    inverse: true,
                    ranges: [
                        {
                            from: -1,
                            to: 0,
                            color: "#FFFFFF",
                            name: "Zero",
                        },
                        {
                            from: 0.0001,
                            to: 1,
                            color: "#071D54",
                            name: "Low",
                        },
                        {
                            from: 1,
                            to: 5,
                            color: "#102A70",
                            name: "Below Average",
                        },
                        {
                            from: 5,
                            to: 10,
                            color: "#1A388C",
                            name: "Average",
                        },
                        {
                            from: 10,
                            to: 20,
                            color: "#2445A8",
                            name: "Above Average",
                        },
                        {
                            from: 20,
                            to: 50,
                            color: "#2D52C4",
                            name: "High",
                        },
                        {
                            from: 50,
                            to: 100,
                            color: "#376FE0",
                            name: "Very High",
                        },
                        {
                            from: 100,
                            to: 200,
                            color: "#418CFC",
                            name: "Extremely High",
                        },
                        {
                            from: 200,
                            to: 500,
                            color: "#5AA0FF",
                            name: "Max",
                        },
                    ],
                },
            },
        },
        xaxis: {
            type: "category",
            categories: districtReports.map(district => district.district || "Unknown"),
            title: {
                text: "District",
                style: {
                    fontSize: '12px',
                    fontWeight: 'bold'
                }
            },
            labels: {
                rotate: -45,
                style: {
                    fontSize: '10px'
                }
            }
        },
        yaxis: {
            title: {
                text: "Metrics",
                style: {
                    fontSize: '12px',
                    fontWeight: 'bold'
                }
            }
        },
        tooltip: {
            y: {
                formatter: (value) => `Count: ${value}`,
            },
        },
    };

    if (loading) {
        return (
            <Box w="100%" p={4}>
                <Text mb={4} fontSize="xl">District Metrics Heatmap</Text>
                <Skeleton height="350px" />
            </Box>
        );
    }

    if (error) {
        return <Text color="red.500">Error loading data: {error}</Text>;
    }

    return (
        <Box w="100%" p={4}>
            <Text mb={4} fontSize="xl">District Metrics Heatmap</Text>
            <Chart
                options={chartOptions}
                series={heatmapData}
                type="heatmap"
                height={350}
            />
        </Box>
    );
};

export default DataByDistrictHeatmap;